import useSWR from 'swr';
import { useAuth } from '@/contexts/auth-context';
import { ProfileService } from '../services/profile';
import type { ProfileData, UsageSummary } from '../types';

interface CachedProfileReturn {
    // Profile data
    profile: ProfileData | null;
    isLoadingProfile: boolean;
    profileError: Error | null;

    // Usage summary data
    usageSummary: UsageSummary | null;
    isLoadingUsageSummary: boolean;
    usageSummaryError: Error | null;

    // Mutation functions
    mutateProfile: () => Promise<any>;
    mutateUsageSummary: () => Promise<any>;
}

/**
 * Cached hook for profile data using SWR
 * Provides instant cached data on subsequent loads with background revalidation
 */
export function useCachedProfile(): CachedProfileReturn {
    const { user } = useAuth();

    // Fetch profile data with SWR caching
    const {
        data: profile,
        error: profileError,
        isLoading: isLoadingProfile,
        mutate: mutateProfile
    } = useSWR(
        user?.id ? ['profile', user.id] : null,
        async ([_, userId]) => {
            const { data, error } = await ProfileService.fetchProfile(userId);
            if (error) throw new Error(error);
            return data;
        },
        {
            revalidateOnFocus: true,
            dedupingInterval: 2000,
            // Keep cached data for 5 minutes
            focusThrottleInterval: 5000,
        }
    );

    // Fetch usage summary with SWR caching
    const {
        data: usageSummary,
        error: usageSummaryError,
        isLoading: isLoadingUsageSummary,
        mutate: mutateUsageSummary
    } = useSWR(
        user?.id ? ['usage-summary', user.id] : null,
        async () => {
            const { data, error } = await ProfileService.fetchUsageSummary();
            if (error) throw new Error(error);
            return data;
        },
        {
            revalidateOnFocus: true,
            dedupingInterval: 2000,
            focusThrottleInterval: 5000,
        }
    );

    return {
        profile: profile ?? null,
        isLoadingProfile,
        profileError: profileError ?? null,

        usageSummary: usageSummary ?? null,
        isLoadingUsageSummary,
        usageSummaryError: usageSummaryError ?? null,

        mutateProfile,
        mutateUsageSummary,
    };
}
